/**
 * Regras puras do wizard de contrato (sem React), usadas pelo index.tsx ao navegar,
 * salvar e validar. Ficam fora da página para teste (review do PR #772, 25/09).
 */
import type {
  IDadosContratoVigente,
  StatusContratoSocietario,
} from '../../../../hooks/useSocietario';
import {
  ISocioFormValues,
  IWizardFormValues,
  socioDefaults,
  TESTE_DA_CESSAO,
  TipoAlteracaoSocio,
} from './schema';
import { aspectoLiberado, ILiberacoesDoPassoSocios } from './regrasDoPassoSocios';

// ── Salvamento ────────────────────────────────────────────────────────────────

// Status em que o "Salvar e sair" grava como rascunho (e notifica o departamento). Fora
// deles o PATCH só grava os dados: gerado -> rascunho é transição inválida (409).
export const STATUS_RASCUNHO_EDITAVEL: readonly StatusContratoSocietario[] = [
  'rascunho_inicial',
  'rascunho',
  'campos_pendentes',
];

export const ehRascunhoEditavel = (status: string | null | undefined): boolean =>
  (STATUS_RASCUNHO_EDITAVEL as readonly string[]).includes(status ?? '');

// Status em que o "Continuar" grava sozinho: os de rascunho e o em_revisao, que o PATCH
// deixa como está. Fora: gerado (o PATCH com dados o rebaixa para em_revisao) e os que
// saíram do fluxo (assinado, cancelado, arquivado).
export const STATUS_COM_SALVAMENTO_AUTOMATICO: readonly StatusContratoSocietario[] = [
  ...STATUS_RASCUNHO_EDITAVEL,
  'em_revisao',
];

export const salvaAoContinuar = (status: string | null | undefined): boolean =>
  (STATUS_COM_SALVAMENTO_AUTOMATICO as readonly string[]).includes(status ?? '');

/** Texto do diálogo de descarte: não afirma que gravou quando o último salvamento falhou. */
export const textoDoDescarte = (autoSaveFalhou: boolean): string =>
  autoSaveFalhou
    ? 'O último salvamento automático falhou: o rascunho pode não ter o que você ' +
      'preencheu. Sair agora descarta o contrato (não dá para desfazer). Use "Salvar e ' +
      'sair" para gravar e mantê-lo.'
    : 'O rascunho foi gravado até o último "Continuar", mas você não usou "Salvar e ' +
      'sair". Sair agora descarta o contrato (não dá para desfazer). Use "Salvar e sair" ' +
      'para mantê-lo.';

// ── Erros de validação ────────────────────────────────────────────────────────

export interface IErroDoPasso {
  /** Caminho no formato do RHF: socios.1.regime_casamento. */
  caminho: string;
  mensagem: string;
  /** Teste do yup que falhou (required, cessao-valida...). */
  tipo?: string;
}

/** Caminho do yup (socios[1].regime_casamento) no formato do RHF (socios.1.regime_casamento). */
export const caminhoDoRhf = (caminho: string): string =>
  caminho
    .replace(/\["([^"]+)"\]/g, '.$1')
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/^\./, '');

/** Os erros folha de uma validação do yup (abortEarly: false). Outro erro: lista vazia. */
export const errosDoYup = (err: any): IErroDoPasso[] => {
  if (err?.name !== 'ValidationError') return [];
  const folhas: any[] = err.inner?.length ? err.inner : [err];
  return folhas
    .filter(e => typeof e?.message === 'string' && e.message.length > 0)
    .map(e => ({
      caminho: caminhoDoRhf(String(e.path ?? '')),
      mensagem: e.message,
      tipo: e.type,
    }));
};

/**
 * O erro do toast: o da cessão, se houver; senão o primeiro. Quem cede demais fica com quota
 * 0, e o "Quotas deve ser maior que zero" (a consequência) vinha antes da causa.
 */
export const erroPrincipal = (erros: IErroDoPasso[]): IErroDoPasso | undefined =>
  erros.find(e => e.tipo === TESTE_DA_CESSAO) ?? erros[0];

/** Erros a marcar no formulário: um por campo, o principal na frente (o campo mostra o do toast). */
export const errosParaMarcar = (erros: IErroDoPasso[]): IErroDoPasso[] => {
  const principal = erroPrincipal(erros);
  const vistos = new Set<string>();
  return (principal ? [principal, ...erros] : erros).filter(e => {
    if (!e.caminho || vistos.has(e.caminho)) return false;
    vistos.add(e.caminho);
    return true;
  });
};

/**
 * Mensagem do toast. No passo Sócios diz de quem é o erro: "Sócio 2 (ANA): Regime
 * inválido". Sem nome no formulário, "Sócio 2".
 */
export const mensagemDoErro = (
  erro: IErroDoPasso | undefined,
  socios: ReadonlyArray<{ nome?: string | null } | null | undefined> | null | undefined,
): string => {
  if (!erro) return 'Preencha os campos obrigatórios';
  const doSocio = /^socios\.(\d+)(\.|$)/.exec(erro.caminho);
  if (!doSocio) return erro.mensagem;
  const indice = Number(doSocio[1]);
  const nome = String(socios?.[indice]?.nome ?? '').trim();
  // A mensagem que já começa pelo nome (ex.: a da cessão) não repete o sócio.
  if (nome && erro.mensagem.startsWith(nome)) return erro.mensagem;
  const quem = nome ? `Sócio ${indice + 1} (${nome})` : `Sócio ${indice + 1}`;
  return `${quem}: ${erro.mensagem}`;
};

// ── CNAEs ─────────────────────────────────────────────────────────────────────

type CnaeTalvez = { codigo?: string | null; descricao?: string | null } | null | undefined;

/** Há ao menos um CNAE com código ou descrição (o seletor guarda linhas vazias). */
export const temCnaePreenchido = (cnaes: ReadonlyArray<CnaeTalvez> | null | undefined): boolean =>
  (cnaes ?? []).some(c => !!(c?.codigo?.trim() || c?.descricao?.trim()));

/** Os CNAEs do vigente para o formulário: os lidos; sem eles, os sugeridos pela IA. */
export const cnaesDoVigente = (
  dados: IDadosContratoVigente | null | undefined,
): { cnaes: IWizardFormValues['cnaes']; sugeridos: boolean } => {
  const lidos = dados?.cnaes_atual ?? [];
  if (lidos.length > 0) {
    return {
      cnaes: lidos.map((c, i) => ({
        codigo: c.codigo,
        descricao: c.descricao,
        principal: i === 0,
      })),
      sugeridos: false,
    };
  }
  const sugeridos = dados?.cnaes_sugeridos ?? [];
  return {
    cnaes: sugeridos.map((c, i) => ({
      codigo: c.codigo,
      descricao: c.descricao,
      principal: c.principal ?? i === 0,
    })),
    sugeridos: sugeridos.length > 0,
  };
};

// ── Sócios: reset ao desmarcar um tipo ────────────────────────────────────────

const soDigitos = (v: unknown): string => String(v ?? '').replace(/\D/g, '');

// Campos de cada aspecto de qualificação (o CAMPOS_DO_ASPECTO do AcaoSocioBlock, mais o
// endereço em texto e o representante da PJ).
const CAMPOS_DA_QUALIFICACAO: Partial<Record<TipoAlteracaoSocio, (keyof ISocioFormValues)[]>> = {
  endereco: ['endereco', 'endereco_estruturado'],
  nome: ['nome'],
  profissao: ['profissao'],
  estado_civil: [
    'estado_civil',
    'regime_casamento',
    'pacto_antenupcial',
    'nome_conjuge',
    'cpf_conjuge',
  ],
  representante: ['representante_nome', 'representante_cpf', 'representante'],
};

// A leitura do vigente não traz estes: voltam ao que o formulário tem antes de qualquer ato.
const valorSemLeitura = (campo: keyof ISocioFormValues): unknown => {
  const padrao = campo === 'representante' ? socioDefaults.representante : null;
  return padrao ? JSON.parse(JSON.stringify(padrao)) : null;
};

/**
 * Tira o sócio `indice` da lista e acerta as cessões dos demais: a linha que ia para ele some
 * e os seguintes descem um. Diferente da lixeira do StepSocios (que deixa a linha sem
 * destinatário para o operador escolher outro): aqui só roda quando entrar e sair deixaram de
 * ser liberados, e aí a cessão também deixou.
 */
export const removerSocioDaLista = (
  socios: ISocioFormValues[],
  indice: number,
): ISocioFormValues[] =>
  socios
    .filter((_, i) => i !== indice)
    .map(s => {
      const redist = s.redistribuicao_quotas;
      if (!redist || redist.length === 0) return s;
      const ajustado = redist
        .filter(d => d?.socio_ordem !== indice)
        .map(d => ({
          socio_ordem:
            d?.socio_ordem != null && d.socio_ordem > indice
              ? d.socio_ordem - 1
              : (d?.socio_ordem ?? null),
          qtd: d?.qtd ?? null,
        }));
      return { ...s, redistribuicao_quotas: ajustado.length > 0 ? ajustado : null };
    });

// Nome sem acento, caixa nem espaço: "Ana  Silva" casa "ANA SILVA". O acento sai pelo
// código (U+0300 a U+036F), como no achatar do derivarPatchesV3.
const nomeAchatado = (v: unknown): string =>
  Array.from(String(v ?? '').normalize('NFD'))
    .filter(c => {
      const cp = c.codePointAt(0) ?? 0;
      return (cp < 0x300 || cp > 0x36f) && c.trim() !== '';
    })
    .join('')
    .toUpperCase();

const docDoSocio = (s: Partial<ISocioFormValues> | null | undefined): string =>
  soDigitos(s?.cpf) || soDigitos(s?.cnpj);

/**
 * Par de cada sócio na leitura, por índice (undefined: sem par). Por CPF/CNPJ; sem documento
 * que case, pelo nome achatado; por último a posição entre quem veio do vigente, se o nome não
 * contradiz. Depois da lixeira numa linha lida sem CPF, a posição sozinha dava o par errado.
 * Cada linha da leitura serve a um sócio só.
 */
const parearComVigente = (
  socios: ISocioFormValues[],
  vigente: Partial<ISocioFormValues>[],
): (Partial<ISocioFormValues> | undefined)[] => {
  const pares: (number | undefined)[] = socios.map(() => undefined);
  const usados = new Set<number>();
  const vemDoVigente = (i: number) => socios[i]?.acao !== 'adicionar';
  const casar = (i: number, criterio: (v: Partial<ISocioFormValues>) => boolean) => {
    const achado = vigente.findIndex((v, k) => !usados.has(k) && criterio(v));
    if (achado < 0) return;
    pares[i] = achado;
    usados.add(achado);
  };
  socios.forEach((s, i) => {
    const doc = docDoSocio(s);
    if (vemDoVigente(i) && doc) casar(i, v => docDoSocio(v) === doc);
  });
  socios.forEach((s, i) => {
    const nome = nomeAchatado(s?.nome);
    if (vemDoVigente(i) && pares[i] === undefined && nome) {
      casar(i, v => nomeAchatado(v.nome) === nome);
    }
  });
  let posicao = 0;
  socios.forEach((s, i) => {
    if (!vemDoVigente(i)) return;
    const k = posicao;
    posicao += 1;
    if (pares[i] !== undefined || k >= vigente.length || usados.has(k)) return;
    // Quem marcou "Nome" trocou o nome de propósito: o dele não serve de prova.
    const renomeou = (s.tipos_alteracao_socio ?? []).includes('nome');
    const nome = nomeAchatado(s.nome);
    const lido = nomeAchatado(vigente[k].nome);
    if (!renomeou && nome && lido && nome !== lido) return;
    pares[i] = k;
    usados.add(k);
  });
  return pares.map(k => (k === undefined ? undefined : vigente[k]));
};

/**
 * Desmarcou um tipo que abria o passo Sócios: o que os tipos que SOBRAM não liberam volta
 * ao vigente, sócio a sócio. Sem entrar/sair, quem entrou sai e quem saía fica; sem cessão,
 * quem continua não cede; sem qualificação, os campos dos aspectos marcados voltam. Quotas
 * e vínculo voltam só em quem tinha o aspecto (ou saía): sem ele, o valor no card é
 * correção da leitura, e não sai. "Alterar" sem aspecto vira "manter".
 *
 * `vigente` é o quadro lido no formato do formulário (construirSociosDoVigente), casado
 * pelo parearComVigente. Leitura sem quotas (nulas ou 0) não sobrescreve o que o operador
 * corrigiu.
 */
export const reverterSociosAoDesmarcar = (
  socios: ISocioFormValues[],
  vigente: Partial<ISocioFormValues>[],
  lib: ILiberacoesDoPassoSocios,
): ISocioFormValues[] => {
  let lista = socios;
  if (!lib.entrarSair) {
    // Do fim para o começo: cada remoção acerta a ordem das cessões dos demais.
    for (let i = lista.length - 1; i >= 0; i -= 1) {
      if (lista[i]?.acao === 'adicionar') lista = removerSocioDaLista(lista, i);
    }
  }
  const pares = parearComVigente(lista, vigente);
  return lista.map((original, i) => {
    if (original.acao === 'adicionar') return original;
    const base = pares[i];
    const s: ISocioFormValues = { ...original };
    const voltou = !lib.entrarSair && s.acao === 'sair';
    if (voltou) {
      s.acao = 'manter';
      s.redistribuicao_quotas = null;
    }
    if (!lib.cessao && s.acao !== 'sair') s.redistribuicao_quotas = null;
    if (!lib.vinculo) s.permanece_como_administrador = false;
    const aspectos = (s.tipos_alteracao_socio ?? []) as TipoAlteracaoSocio[];
    const ficam = aspectos.filter(a => aspectoLiberado(a, lib));
    const saem = aspectos.filter(a => !ficam.includes(a));
    if (base) {
      const volta = (aspecto: TipoAlteracaoSocio, liberado: boolean) =>
        !liberado && (saem.includes(aspecto) || voltou);
      if (volta('quotas', lib.quotas) && Number(base.quotas) > 0) s.quotas = Number(base.quotas);
      if (volta('vinculo', lib.vinculo) && base.tipo_vinculo) s.tipo_vinculo = base.tipo_vinculo;
      const campos = s as unknown as Record<string, unknown>;
      saem.forEach(aspecto =>
        (CAMPOS_DA_QUALIFICACAO[aspecto] ?? []).forEach(campo => {
          campos[campo] = campo in base ? base[campo] : valorSemLeitura(campo);
        }),
      );
    }
    if (s.acao === 'alterar' && ficam.length === 0) s.acao = 'manter';
    s.tipos_alteracao_socio = s.acao === 'alterar' ? ficam : null;
    return s;
  });
};
